import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import VendorFormPage from "./AddVendor";
import {
  useGetVendorsQuery,
  useDeleteVendorMutation,
} from "../../../redux/api/vendor";
import { getVendorColumns } from "./Data";
import { Add, Business, WarningAmber } from "@mui/icons-material";
import { MuiTable } from "../../../components/MuiTable";

const Vendor = () => {
  const theme = useTheme();
  const currentAgency = useSelector(selectCurrentagency);
  const [mode, setMode] = useState("add");
  const [editRow, setEditRow] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const { data: vendors = [], isLoading, refetch } = useGetVendorsQuery(
    currentAgency?.agencyid
  );
  const [deleteVendor, { isLoading: isDeleting }] = useDeleteVendorMutation();

  // Edit Handler
  const handleEdit = (row) => {
    setMode("edit");
    setEditRow(row);
    setOpen(true);
  };

  // Delete Handler (opens confirmation popup)
  const handleDelete = (row) => {
    setSelectedVendor(row);
    setDeleteDialogOpen(true);
  };

  // Confirm Deletion
  const confirmDelete = async () => {
    if (!selectedVendor) return;
    try {
      await deleteVendor(selectedVendor.id).unwrap();
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
      refetch();
    } catch (err) {
      console.error("Error deleting vendor:", err);
    }
  };

  const columns = useMemo(() => getVendorColumns(handleEdit, handleDelete), []);

  if (!currentAgency?.agencyid) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Please select an agency first.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3, }, minHeight: "100vh" }}>
      {/* Header */}
      <Card
        sx={{
          mb: 4,
          p: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Business sx={{ fontSize: 32 }} />
                Vendor Management
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Manage your real estate vendors and partners
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={`${vendors.length} Vendors`}
            sx={{
              background: "rgba(255,255,255,0.2)",
              color: "white",
              fontWeight: "bold",
              fontSize: "1rem",
            }}
          />
        </Stack>
      </Card>

      {/* Action Bar */}
      <Card sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="600">
            All Vendors
          </Typography>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => {
              setMode("add");
              setEditRow(null);
              setOpen(true);
            }}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              borderRadius: 2,
              px: 3,
              fontWeight: "bold",
            }}
          >
            Add Vendor
          </Button>
        </Stack>
      </Card>

      {/* Data Table */}
      <Card
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <MuiTable
          data={vendors}
          isLoading={isLoading}
          columns={columns}
          enableRowSelection={false}
          enableActionButton={false}
          enableColor={false}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            fontWeight: "bold",
          }}
        >
          {mode === "edit" ? "Edit Vendor" : "Add New Vendor"}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <VendorFormPage
            mode={mode}
            editRow={editRow}
            onSuccess={() => {
              setMode("add");
              setEditRow(null);
              setOpen(false);
              refetch();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmber color="warning" />
            <Typography variant="h6" fontWeight="bold">
              Confirm Deletion
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ color: "text.secondary", mt: 1 }}>
            Are you sure you want to delete{" "}
            <Typography component="span" fontWeight="bold" color="error.main">
              {selectedVendor?.name || "this vendor"}
            </Typography>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", p: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Vendor;
